// ABOUTME: Game management page for the game owner.
// ABOUTME: Shows title, status, lifecycle controls, player count, and share invite link.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router'
import { useAuth } from '@clerk/react'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { HostPickGrid } from '../components/HostPickGrid'
import { CalledSquareBalls } from '../components/CalledSquareBalls'
import { Pagination } from '../components/Pagination'
import { ShareLinkDialog } from '../components/ShareLinkDialog'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import IosShareIcon from '@mui/icons-material/IosShare'
import { FormCard, FormCardHeader, FormCardBody, FormCardFooter, FormCardHeaderStart, FormCardHeaderEnd, InlineContent } from '../theme'
import { STATUS_LABEL, STATUS_COLOR } from '../components/gameStatus'
import {
  gameActionsControllerGetGame,
  gameActionsControllerUpdateGameStatus,
  gameActionsControllerGetGamePlayers,
  gameActionsControllerRemovePlayer,
  gameCallerActionsControllerGetCalledSquares,
  gameCallerActionsControllerCallSquare,
  squarePacksControllerGetSquares,
  billingControllerCreateCheckout,
  GameDtoStatus,
  UpdateGameStatusDtoStatus,
  type GameDto,
  type CalledGameSquare,
  type GameSquareDto,
  type GamePlayerDto,
} from '../api/generated'
import { useGameRoom } from '../hooks/useGameRoom'
import { trackPlayerAdded, trackGameFinished, trackShareLinkCopied } from '../analytics/events'

function lifecycleAction(status: GameDto['status']): { label: string; next: UpdateGameStatusDtoStatus } | null {
  if (status === GameDtoStatus.DRAFT) return { label: 'Open Game', next: UpdateGameStatusDtoStatus.OPEN }
  if (status === GameDtoStatus.OPEN) return { label: 'Start Game', next: UpdateGameStatusDtoStatus.ACTIVE }
  if (status === GameDtoStatus.ACTIVE) return { label: 'End Game', next: UpdateGameStatusDtoStatus.ENDED }
  return null
}

export function GameManagePage() {
  const { id } = useParams<{ id: string }>()
  const { getToken } = useAuth()
  const [game, setGame] = useState<GameDto | null>(null)
  const [players, setPlayers] = useState<GamePlayerDto[]>([])
  const [calledSquares, setCalledSquares] = useState<CalledGameSquare[]>([])
  const [packSquares, setPackSquares] = useState<GameSquareDto[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [calledSquaresPage, setCalledSquaresPage] = useState(1)
  const [calledSquaresTotalPages, setCalledSquaresTotalPages] = useState(1)
  const [winnerName, setWinnerName] = useState<string | null>(null)
  const [lifecycleError, setLifecycleError] = useState<string | false>(false)
  const [shareOpen, setShareOpen] = useState(false)
  const pendingOrCalledSquareIds = useRef(new Set<string>())
  const removingPlayerIds = useRef(new Set<string>())
  const [optimisticallyCalledIds, setOptimisticallyCalledIds] = useState(() => new Set<string>())
  const [upgradeNeeded, setUpgradeNeeded] = useState<{
    neededTier: 'party_pass' | 'mega_party'
    gameId: string
  } | null>(null)
  const handleEvent = useCallback((event: string, data: unknown) => {
    if (event === 'square.called') {
      setRefreshKey((k) => k + 1)
    }
    if (event === 'bingo.won') {
      const { gamePlayerId } = data as { gamePlayerId: string }
      const winner = players.find((p) => p.id === gamePlayerId)
      setWinnerName(winner?.firstName ?? 'A player')
    }
    if (event === 'player.joined') {
      const { gamePlayer } = data as { gamePlayer: GamePlayerDto }
      setPlayers((prev) => [...prev, gamePlayer])
      trackPlayerAdded(id!, players.length + 1)
    }
    if (event === 'player.removed') {
      const { gamePlayerId } = data as { gamePlayerId: string }
      setPlayers((prev) => prev.filter((p) => p.id !== gamePlayerId))
    }
    if (event === 'upgrade.required') {
      const { neededTier, gameId: gId } = data as { neededTier: 'party_pass' | 'mega_party'; gameId: string }
      setUpgradeNeeded({ neededTier, gameId: gId })
    }
    if (event === 'tier.upgraded') {
      setUpgradeNeeded(null)
    }
  }, [players, id])

  useGameRoom({ gameId: id!, onEvent: handleEvent })

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken()
        const [gameRes, calledRes, playersRes] = await Promise.all([
          gameActionsControllerGetGame(id!, { headers: { Authorization: `Bearer ${token}` } }),
          gameCallerActionsControllerGetCalledSquares(id!, { page: calledSquaresPage, limit: 50 }, { headers: { Authorization: `Bearer ${token}` } }),
          gameActionsControllerGetGamePlayers(id!, { page: 1, limit: 100 }, { headers: { Authorization: `Bearer ${token}` } }),
        ])
        if (gameRes.status === 200) {
          setGame(gameRes.data)
          const squaresRes = await squarePacksControllerGetSquares(
            gameRes.data.squarePackId,
            { page: 1, limit: 100 },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          if (squaresRes.status === 200) {
            setPackSquares(squaresRes.data.items as unknown as GameSquareDto[])
          }
        }
        if (calledRes.status === 200) {
          const items = calledRes.data.items as CalledGameSquare[]
          setCalledSquares(items)
          setCalledSquaresTotalPages(calledRes.data.totalPages)
          pendingOrCalledSquareIds.current = new Set(
            items.flatMap((cs) => {
              const gsId = (cs as Record<string, unknown>)['gameSquareId'] as string | undefined
              return gsId ? [gsId] : []
            })
          )
        }
        if (playersRes.status === 200) {
          setPlayers(playersRes.data.items as unknown as GamePlayerDto[])
        }
      } catch {
        setLifecycleError('Could not load game data. Please refresh the page.')
      }
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshKey, calledSquaresPage])

  async function handleLifecycle(next: UpdateGameStatusDtoStatus) {
    try {
      const token = await getToken()
      await gameActionsControllerUpdateGameStatus(
        id!,
        { status: next },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (next === UpdateGameStatusDtoStatus.ENDED) trackGameFinished(id!)
      setRefreshKey((k) => k + 1)
    } catch {
      setLifecycleError('Could not update game status. Make sure the square pack has at least 25 squares.')
    }
  }

  async function handleCallSquare() {
    try {
      const token = await getToken()
      await gameCallerActionsControllerCallSquare(id!, {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      setLifecycleError('Failed to call square. Please try again.')
    }
  }

  async function handleCallHostPickSquare(squareId: string) {
    if (pendingOrCalledSquareIds.current.has(squareId)) return
    pendingOrCalledSquareIds.current.add(squareId)
    setOptimisticallyCalledIds((prev) => new Set([...prev, squareId]))

    const token = await getToken()
    try {
      const result = await gameCallerActionsControllerCallSquare(
        id!,
        { squareId },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (result.status === 201) {
        setCalledSquares((prev) => [...prev, result.data as CalledGameSquare])
        setOptimisticallyCalledIds((prev) => { const next = new Set(prev); next.delete(squareId); return next })
      } else {
        pendingOrCalledSquareIds.current.delete(squareId)
        setOptimisticallyCalledIds((prev) => { const next = new Set(prev); next.delete(squareId); return next })
      }
    } catch {
      pendingOrCalledSquareIds.current.delete(squareId)
      setOptimisticallyCalledIds((prev) => { const next = new Set(prev); next.delete(squareId); return next })
    }
  }

  async function handleRemovePlayer(gamePlayerId: string) {
    if (removingPlayerIds.current.has(gamePlayerId)) return
    removingPlayerIds.current.add(gamePlayerId)
    try {
      const token = await getToken()
      await gameActionsControllerRemovePlayer(id!, gamePlayerId, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      removingPlayerIds.current.delete(gamePlayerId)
      setLifecycleError('Could not remove player. Please try again.')
    }
  }

  async function handleUpgrade(targetTier: 'party_pass' | 'mega_party', gameId: string) {
    try {
      const token = await getToken()
      const result = await billingControllerCreateCheckout(
        { targetTier, gameId },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      window.location.href = result.data.url
    } catch {
      setLifecycleError('Unable to start checkout. Please try again.')
    }
  }

  if (!game) {
    return lifecycleError ? (
      <Snackbar open onClose={() => setLifecycleError(false)}>
        <Alert severity="error" onClose={() => setLifecycleError(false)}>
          {lifecycleError}
        </Alert>
      </Snackbar>
    ) : null
  }

  const action = lifecycleAction(game.status)
  const squareCount = game.squarePack.squareCount
  const squaresNeeded = action?.next === UpdateGameStatusDtoStatus.OPEN ? Math.max(0, 25 - squareCount) : 0
  const needsPlayers = action?.next === UpdateGameStatusDtoStatus.ACTIVE && players.length === 0
  const joinUrl = `${window.location.origin}/join/${game.shortId ?? game.id}`

  return (
    <Stack spacing={3} pt={4} alignItems="center">
      {upgradeNeeded && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              onClick={() => handleUpgrade(upgradeNeeded.neededTier, upgradeNeeded.gameId)}
            >
              {upgradeNeeded.neededTier === 'party_pass'
                ? 'Upgrade to Party Pass ($5)'
                : 'Upgrade to Mega Party ($10)'}
            </Button>
          }
        >
          A player couldn't join — your game is full. Upgrade to allow more players.
        </Alert>
      )}
      <FormCard>
        <FormCardHeader>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormCardHeaderStart direction="row" alignItems="center" spacing={1}>
              <IconButton color="inherit" aria-label="back" component={Link} to="/games">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1" noWrap>{game.title}</Typography>
            </FormCardHeaderStart>
            <FormCardHeaderEnd direction="row" spacing={1} alignItems="center">
              {action && (
                <Button color="inherit" variant="outlined" size="small" disabled={squaresNeeded > 0 || needsPlayers} onClick={() => handleLifecycle(action.next)}>
                  {action.label}
                </Button>
              )}
            </FormCardHeaderEnd>
          </Stack>
        </FormCardHeader>
        <FormCardBody>
          <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <InlineContent>
              <Chip
                label={STATUS_LABEL[game.status]}
                color={STATUS_COLOR[game.status]}
                size="small"
              />
            </InlineContent>
            {game.status !== GameDtoStatus.ENDED && (
              <Button color="primary" variant="outlined" size="small" startIcon={<IosShareIcon />} onClick={() => setShareOpen(true)}>
                Share Invite
              </Button>
            )}
          </Stack>
          {squaresNeeded > 0 && (
            <Alert severity="warning">
              This pack needs {squaresNeeded} more {squaresNeeded === 1 ? 'square' : 'squares'} before you can open the game (minimum 25).{' '}
              <Link to={`/square-packs/${game.squarePackId}`}>Add squares to this pack.</Link>
            </Alert>
          )}
          {needsPlayers && (
            <Alert severity="warning">
              Add at least one player before starting the game.
            </Alert>
          )}
          <Typography variant="h6">Players</Typography>
          <Typography variant="body2" color="text.secondary">
            {players.length} {players.length === 1 ? 'player' : 'players'} joined
          </Typography>
          {players.length > 0 && (
            <List dense disablePadding>
              {players.map((p) => (
                <ListItem
                  key={p.id}
                  disableGutters
                  secondaryAction={
                    game.status !== GameDtoStatus.ENDED && (
                      <IconButton
                        edge="end"
                        aria-label="remove"
                        onClick={() => handleRemovePlayer(p.id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText primary={p.firstName} />
                </ListItem>
              ))}
            </List>
          )}
          </Stack>
        </FormCardBody>
      </FormCard>

      {(game.status === GameDtoStatus.ACTIVE || game.status === GameDtoStatus.ENDED) && (
        <FormCard>
          <FormCardHeader>
            <Typography variant="h5" component="h2">Called Squares</Typography>
          </FormCardHeader>
          <FormCardBody>
            <Stack spacing={3}>
            {game.squarePack.randomCalled ? (
              <Stack alignItems="flex-end">
                <Button
                  color="inherit"
                  variant="outlined"
                  disabled={game.status !== GameDtoStatus.ACTIVE}
                  onClick={handleCallSquare}
                >
                  Call Next Square
                </Button>
              </Stack>
            ) : (
              (game.status === GameDtoStatus.ACTIVE || game.status === GameDtoStatus.ENDED) && (
                <HostPickGrid
                  packSquares={packSquares}
                  calledSquares={calledSquares}
                  onCallSquare={game.status === GameDtoStatus.ACTIVE ? handleCallHostPickSquare : undefined}
                  optimisticallyCalledIds={optimisticallyCalledIds}
                />
              )
            )}
            {game.squarePack.randomCalled && (
              calledSquares.length === 0 ? (
                <Typography color="text.secondary">No squares called yet.</Typography>
              ) : (
                <CalledSquareBalls squares={calledSquares.map((sq, i) => {
                  const called = sq as Record<string, unknown>
                  const gameSquare = called['gameSquare'] as Record<string, unknown> | undefined
                  return {
                    id: (called['id'] as string) ?? String(i),
                    title: (gameSquare?.['title'] as string) ?? '—',
                  }
                })} />
              )
            )}
            </Stack>
          </FormCardBody>
          <FormCardFooter>
            <Pagination page={calledSquaresPage} totalPages={calledSquaresTotalPages} onChange={setCalledSquaresPage} />
          </FormCardFooter>
        </FormCard>
      )}

      {game && (
        <ShareLinkDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          url={joinUrl}
          title="Invite Players"
          description="Share this link with players so they can join:"
          onCopy={() => trackShareLinkCopied('game', game.id)}
        />
      )}

      <Dialog open={winnerName !== null} aria-labelledby="bingo-won-title" aria-describedby="bingo-won-description" disableEscapeKeyDown>
        <DialogTitle id="bingo-won-title">🎉 We have a winner!</DialogTitle>
        <DialogContent>
          <Typography id="bingo-won-description">{winnerName} has won the game</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWinnerName(null)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!lifecycleError} onClose={() => setLifecycleError(false)}>
        <Alert severity="error" onClose={() => setLifecycleError(false)}>
          {lifecycleError}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
